import { useCallback } from "react";
import { usePostHog } from "posthog-js/react";

/**
 * Thin analytics wrapper around PostHog. Every custom client event flows through
 * here. When PostHog isn't mounted (non-production builds — see index.tsx) the
 * value from usePostHog() is undefined, so every call safely no-ops.
 *
 * - capture: a named lifecycle/funnel event (e.g. "checkout_started").
 * - trackFeature: feature-usage tracking. One event ("feature_used") with the
 *   surface in the `feature` property, so PostHog can break usage down by
 *   feature (most vs least used) AND count the event itself as an engagement
 *   signal — without scattering dozens of bespoke event names.
 */
export const useAnalytics = () => {
  const posthog = usePostHog();

  const capture = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      posthog?.capture(event, properties);
    },
    [posthog],
  );

  const trackFeature = useCallback(
    (feature: string, properties?: Record<string, unknown>) => {
      posthog?.capture("feature_used", { feature, ...properties });
    },
    [posthog],
  );

  return { capture, trackFeature };
};
